/**
 * Письмо-напоминание: шаблон, подстановки и ссылка на окно Gmail.
 *
 * Отдельным модулем, а не внутри компонента: правила подстановки — это
 * поведение, которое проверяется тестом, а не разметка. Отправки здесь нет
 * и быть не может: письмо открывается в окне Gmail, и что человек с ним
 * сделает, экран не знает (решение 2026-09-23).
 */

export interface LetterVars {
  name: string;
  month: string;
  jiraHours: number;
  /** `null` — человека нет в табеле месяца: часы PeopleForce неизвестны. */
  hrHours: number | null;
  remainderHours: number | null;
}

/**
 * Письмо идёт **по-украински** (решение заказчика 2026-09-23): это письмо
 * сотруднику, а не экран администратора. Поэтому и поля шаблона украинские —
 * они стоят внутри украинского текста, и смесь языков в одной строке читалась
 * бы как ошибка. Интерфейс вокруг остаётся русским, как и весь дашборд.
 */
export const PLACEHOLDERS = [
  "{ім'я}",
  '{місяць}',
  '{години_jira}',
  '{години_табель}',
  '{не_рознесено}',
] as const;

/** Месяцы для письма — украинские: русские в украинском тексте выглядят опиской. */
const MONTHS_UA = [
  'січень',
  'лютий',
  'березень',
  'квітень',
  'травень',
  'червень',
  'липень',
  'серпень',
  'вересень',
  'жовтень',
  'листопад',
  'грудень',
];

/** `2026-09-01` → `вересень 2026`. Для подстановки в письмо, не для экрана. */
export function monthLabelUa(from: string): string {
  const date = new Date(`${from}T00:00:00Z`);
  return `${MONTHS_UA[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/**
 * Текст письма — **дружеский, на «ти»** (правка заказчика 2026-10-09, текст
 * прислан целиком).
 *
 * До этого письмо было требовательным и на «ви», с подписью «адміністрація
 * Govitall»: предполагалось, что требование исходит от порядка в компании,
 * а не лично от того, кто нажал кнопку. Заказчик выбрал другой тон — просьбу
 * от коллег, с благодарностью вместо нажима. Повод тот же (часы не разнесены),
 * разговор другой.
 *
 * Подписи от администрации больше нет: письмо заканчивается «Дякуємо від усієї
 * команди» — это и есть подпись, и ставить над ней вторую, официальную, значило
 * бы спорить с собственным тоном.
 *
 * Чисел и месяца в письме по-прежнему нет (решение 2026-10-08): число верно
 * ровно в секунду нажатия, а отчитывается экран. Про конец месяца сказано
 * без даты — «місяць добігає кінця»: это о поводе написать, а не утверждение
 * о календаре, и устареть такая фраза не может.
 */
export const DEFAULT_SUBJECT = '\u{1F44B} Час перевірити трекінг у Jira';

export const DEFAULT_BODY = `Привіт! \u{1F44B}
Місяць добігає кінця, тож саме час перевірити трекінг у Jira.
Будь ласка, внеси всі години до кінця місяця, щоб дані коректно відобразилися в Sharing Resource \u{1F4CA}
Якщо якісь задачі ще не затрекані, саме час це зробити \u{1F64C}

Цінуємо твою відповідальність. Дякуємо від усієї команди! \u{1F49B}`;

/** Неизвестное число — прочерк, а не ноль: ноль здесь был бы утверждением. */
function hours(value: number | null): string {
  return value === null ? '—' : String(Math.round(value));
}

/**
 * Подстановка значений в шаблон. Неизвестное поле остаётся как есть:
 * опечатку в `{имя}` видно в предпросмотре, а молча вырезанный кусок — нет.
 */
export function renderLetter(template: string, vars: LetterVars): string {
  const values: Record<string, string> = {
    "{ім'я}": vars.name,
    '{місяць}': vars.month,
    '{години_jira}': hours(vars.jiraHours),
    '{години_табель}': hours(vars.hrHours),
    '{не_рознесено}': hours(vars.remainderHours),
  };

  return template.replace(/\{[^{}]+\}/g, (match) => values[match] ?? match);
}

/**
 * Ссылка на окно написания письма в веб-Gmail (решение заказчика 2026-09-23).
 *
 * Не `mailto`: почтовый клиент на машине может быть не настроен вовсе или
 * открыть не ту учётку, а почта здесь корпоративная и живёт в браузере.
 * Адрес отправителя Gmail подставляет сам — тот, под которым человек вошёл.
 *
 * Кодируем и тему, и тело: перенос строки и кириллица без кодирования
 * доезжают искажёнными, а тело письма — это как раз многострочный текст.
 */
export function gmailComposeUrl(email: string, subject: string, body: string): string {
  const query = new URLSearchParams({
    view: 'cm',
    fs: '1',
    to: email,
    su: subject,
    body,
  });
  // `URLSearchParams` кодирует пробел как «+». Gmail его понимает, но в теме
  // письма плюс вместо пробела виден человеку — и это его письмо, не наше.
  return `https://mail.google.com/mail/?${query.toString().replace(/\+/g, '%20')}`;
}
